import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import './Admin.css';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const response = await adminAPI.listUsers();
        setUsers(response.data.users);
      } else {
        const response = await adminAPI.listModels();
        setModels(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // User actions
  const openCreditModal = (user) => {
    setSelectedItem(user);
    setFormData({ amount: 0, reason: '' });
    setModalType('credits');
    setError('');
    setShowModal(true);
  };

  const handleToggleActive = async (user) => {
    try {
      await adminAPI.toggleUserActive(user.id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleUpdateCredits = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await adminAPI.updateCredits(selectedItem.id, formData.amount, formData.reason);
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update credits');
    } finally {
      setSubmitting(false);
    }
  };

  // Model actions
  const openModelModal = (model = null) => {
    if (model) {
      setFormData({
        name: model.name,
        model_id: model.model_id,
        provider: model.provider,
        credit_cost: model.credit_cost,
        description: model.description || '',
        api_key: '',
        is_active: model.is_active,
      });
      setSelectedItem(model);
    } else {
      setFormData({
        name: '',
        model_id: '',
        provider: 'openai',
        credit_cost: 5,
        description: '',
        api_key: '',
        is_active: true,
      });
      setSelectedItem(null);
    }
    setModalType('model');
    setError('');
    setShowModal(true);
  };

  const handleSaveModel = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const data = { ...formData };
      if (!data.api_key) delete data.api_key;

      if (selectedItem) {
        await adminAPI.updateModel(selectedItem.id, data);
      } else {
        await adminAPI.createModel(data);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save model');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteModel = async (model) => {
    if (!window.confirm(`Delete model "${model.name}"?`)) return;

    try {
      await adminAPI.deleteModel(model.id);
      fetchData();
    } catch (error) {
      alert('Failed to delete model');
    }
  };

  return (
    <div className="admin-page">
      <h1>Admin Panel</h1>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`tab ${activeTab === 'models' ? 'active' : ''}`}
          onClick={() => setActiveTab('models')}
        >
          AI Models
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : activeTab === 'users' ? (
        <div className="admin-section">
          <h2>User Management</h2>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Email</th>
                <th>Credits</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.credits}</td>
                  <td>
                    <span className={`role-badge ${user.is_admin ? 'admin' : 'user'}`}>
                      {user.is_admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>
                    <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td className="actions-cell">
                    <button onClick={() => openCreditModal(user)} className="action-btn" title="Manage Credits">
                      💳
                    </button>
                    <button onClick={() => handleToggleActive(user)} className="action-btn" title="Toggle Active">
                      {user.is_active ? '🔒' : '🔓'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-section">
          <div className="section-header">
            <h2>AI Model Configuration</h2>
            <button onClick={() => openModelModal()} className="btn-primary">
              + Add Model
            </button>
          </div>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Model ID</th>
                <th>Provider</th>
                <th>Credit Cost</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model) => (
                <tr key={model.id}>
                  <td>{model.name}</td>
                  <td className="model-id">{model.model_id}</td>
                  <td>
                    <span className={`provider-badge ${model.provider}`}>
                      {model.provider}
                    </span>
                  </td>
                  <td>{model.credit_cost}</td>
                  <td>
                    <span className={`status ${model.is_active ? 'active' : 'inactive'}`}>
                      {model.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button onClick={() => openModelModal(model)} className="action-btn" title="Edit">
                      ✏️
                    </button>
                    <button onClick={() => handleDeleteModel(model)} className="action-btn delete" title="Delete">
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Credits Modal */}
      {showModal && modalType === 'credits' && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Manage Credits - {selectedItem?.username}</h2>
            <p className="current-balance">Current Balance: <strong>{selectedItem?.credits}</strong> credits</p>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleUpdateCredits}>
              <div className="form-group">
                <label htmlFor="amount">Amount</label>
                <input
                  type="number"
                  id="amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseInt(e.target.value) || 0 })}
                  placeholder="Enter amount (positive to add, negative to subtract)"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="reason">Reason</label>
                <input
                  type="text"
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="e.g., Bonus credits, Refund"
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Updating...' : 'Update Credits'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Model Modal */}
      {showModal && modalType === 'model' && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{selectedItem ? 'Edit Model' : 'Add New Model'}</h2>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSaveModel}>
              <div className="form-group">
                <label htmlFor="name">Display Name</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., GPT-4o"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="model_id">Model ID</label>
                <input
                  type="text"
                  id="model_id"
                  value={formData.model_id}
                  onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                  placeholder="e.g., gpt-4o"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="provider">Provider</label>
                <select
                  id="provider"
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="api_key">API Key {selectedItem && '(leave blank to keep current)'}</label>
                <input
                  type="password"
                  id="api_key"
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  placeholder="sk-..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="credit_cost">Credit Cost</label>
                <input
                  type="number"
                  id="credit_cost"
                  value={formData.credit_cost}
                  onChange={(e) => setFormData({ ...formData, credit_cost: parseInt(e.target.value) || 1 })}
                  min="1"
                  max="100"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <input
                  type="text"
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active (available for users)
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Model'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
